import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { usePasswordStrength } from '@/hooks/usePasswordStrength'
import { useToast } from '@/hooks/useToast'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react'

export function SetupPasswordPage() {
  const { user, adminUser, isLoading } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const strength = usePasswordStrength(password)
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0
  const canSubmit = strength.isValid && passwordsMatch && !isSubmitting

  // Guard: no session → login
  if (!isLoading && !user) {
    return <Navigate to="/login" replace />
  }

  // Guard: user already has a password set → redirect to destination
  if (!isLoading && user?.app_metadata?.has_password) {
    return <Navigate to={adminUser?.role === 'investor' ? '/investor' : '/dashboard'} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setIsSubmitting(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
        return
      }

      // Setup-password is effectively the user's first sign-in. The normal
      // signIn() / verifyMfa() paths in AuthContext aren't used here, so we
      // need to record this event manually. Each step is fire-and-forget so
      // one server-side failure doesn't block the user's entry into the app.

      // 1. Mark the pending admin_invites row as accepted. Needs a SECURITY
      //    DEFINER RPC because the current RLS policy only allows super_admin
      //    to update admin_invites.
      try {
        await supabase.rpc('mark_invite_accepted')
      } catch (rpcErr) {
        console.error('Failed to mark invite accepted:', rpcErr)
      }

      // 2. Record a login_success audit event so Per-User Metrics reflects this.
      if (user) {
        try {
          await supabase.rpc('log_admin_audit_event', {
            p_action: 'login_success',
            p_details: { via: 'password_setup' },
            p_user_agent: navigator.userAgent,
          })
        } catch (rpcErr) {
          console.error('Failed to log audit event:', rpcErr)
        }

        // 3. Update admin_users.last_login so the Users page shows a real
        //    timestamp instead of "Never".
        try {
          await supabase
            .from('admin_users')
            .update({ last_login: new Date().toISOString() })
            .eq('id', user.id)
        } catch (updateErr) {
          console.error('Failed to update last_login:', updateErr)
        }
      }

      toast({
        title: 'Password set',
        description: 'Your password has been created successfully.',
      })

      navigate(adminUser?.role === 'investor' ? '/investor' : '/dashboard')
    } catch {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-dextoro/5 via-transparent to-transparent" />
      <div className="relative z-10 w-full max-w-md">
        {/* Branding */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img src="/dextoro-logo.png" alt="dextoro" className="hidden dark:block h-9" />
          <img src="/dextoro-logo-black.png" alt="dextoro" className="block dark:hidden h-9" />
          <p className="text-sm text-muted-foreground mt-2">Private metrics dashboard</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Set Your Password</CardTitle>
            <CardDescription>
              Create a password for your dextoro dashboard account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Strength bar */}
              {password.length > 0 && (
                <div className="space-y-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          i <= strength.score - 1 ? strength.color : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Strength: <span className="font-medium">{strength.label}</span>
                  </p>
                  {strength.feedback.warning && (
                    <p className="text-xs text-destructive">{strength.feedback.warning}</p>
                  )}
                  {strength.feedback.suggestions[0] && !strength.isValid && (
                    <p className="text-xs text-muted-foreground">{strength.feedback.suggestions[0]}</p>
                  )}
                </div>
              )}

              {/* Confirm password */}
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={!canSubmit}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting password...
                  </>
                ) : (
                  'Set Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
